import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Save, CheckCircle, Settings as SettingsIcon, Upload, Trash2, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

export default function Settings() {
  const [form, setForm] = useState({
    company_name: "",
    default_covid_protocols: "",
    default_safety_notes: "",
    default_hospital_name: "",
    default_hospital_phone: "",
    logo_data_uri: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`, ax()).then(r => setForm(f => ({ ...f, ...r.data }))).catch(() => {});
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return alert("Logo must be smaller than 2MB");
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/settings/logo`, fd, { withCredentials: true });
      setForm(f => ({ ...f, logo_data_uri: data.logo_data_uri }));
    } catch (e) {
      alert(e.response?.data?.detail || "Logo upload failed");
    } finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove production logo?")) return;
    await axios.delete(`${API}/settings/logo`, ax());
    setForm(f => ({ ...f, logo_data_uri: "" }));
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setSaved(false);
    try {
      await axios.put(`${API}/settings`, form, ax());
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500 transition-colors";
  const textareaCls = `${inputCls} resize-none`;

  return (
    <div className="p-6 md:p-8 max-w-2xl space-y-8">
      <div>
        <p className="text-xs text-amber-400/80 uppercase tracking-widest mb-1 font-mono">Configuration</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-white font-[Outfit] tracking-tight">Settings</h1>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Company */}
        <div className="bg-[#1A1F29] border border-[#28303F] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white uppercase tracking-widest font-mono mb-4">Production Company</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Company Name</label>
              <input data-testid="settings-company-name-input" value={form.company_name} onChange={set("company_name")}
                placeholder="e.g. Forced Perspective TV" className={inputCls} />
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Production Logo</label>
              <p className="text-xs text-gray-600 mb-3">Appears on the PDF call sheet header and distribution emails. PNG or JPG, max 2MB.</p>

              {form.logo_data_uri ? (
                <div className="flex items-center gap-4 bg-[#0D0F12] border border-[#28303F] rounded-lg p-4">
                  <div className="bg-white rounded-md p-2 flex items-center justify-center" style={{ minWidth: "120px", height: "70px" }}>
                    <img data-testid="settings-logo-preview" src={form.logo_data_uri} alt="Production logo" style={{ maxHeight: "58px", maxWidth: "160px", objectFit: "contain" }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white">Logo uploaded</p>
                    <p className="text-xs text-gray-500 mt-0.5">Every new PDF & email will use this logo.</p>
                  </div>
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingLogo}
                    className="text-xs text-amber-400 hover:text-amber-300 border border-amber-500/30 px-3 py-1.5 rounded-lg">
                    {uploadingLogo ? "..." : "Replace"}
                  </button>
                  <button type="button" onClick={removeLogo}
                    data-testid="settings-logo-remove-btn"
                    className="text-gray-500 hover:text-red-400 p-1.5">
                    <Trash2 size={15} />
                  </button>
                </div>
              ) : (
                <button type="button" data-testid="settings-logo-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="w-full flex items-center justify-center gap-2 bg-[#0D0F12] border border-dashed border-[#28303F] hover:border-amber-500/50 hover:bg-[#0D0F12]/50 rounded-lg py-6 text-gray-400 text-sm transition-all disabled:opacity-50">
                  <Upload size={16} className="text-amber-400" />
                  {uploadingLogo ? "Uploading..." : "Upload Production Logo"}
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
                className="hidden" data-testid="settings-logo-file-input" />
            </div>
          </div>
        </div>

        {/* Default Hospital */}
        <div className="bg-[#1A1F29] border border-[#28303F] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white uppercase tracking-widest font-mono mb-4">Default Nearest Hospital</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Hospital Name</label>
              <input value={form.default_hospital_name} onChange={set("default_hospital_name")} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Phone</label>
              <input value={form.default_hospital_phone} onChange={set("default_hospital_phone")} className={inputCls} />
            </div>
          </div>
        </div>

        {/* Default Notes */}
        <div className="bg-[#1A1F29] border border-[#28303F] rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-white uppercase tracking-widest font-mono">Default Notes</h3>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Default Safety Notes</label>
            <textarea rows={3} value={form.default_safety_notes} onChange={set("default_safety_notes")} className={textareaCls}
              placeholder="Default safety notes that appear on every call sheet..." />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Default COVID-19 Protocols</label>
            <textarea rows={4} value={form.default_covid_protocols} onChange={set("default_covid_protocols")} className={textareaCls}
              placeholder="Default COVID-19 protocols..." />
          </div>
        </div>

        <button data-testid="settings-save-btn" type="submit" disabled={saving}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold px-6 py-3 rounded-lg text-sm transition-all">
          {saved ? <><CheckCircle size={15}/> Saved!</> : <><Save size={15}/> {saving ? "Saving..." : "Save Settings"}</>}
        </button>
      </form>
    </div>
  );
}
